/**
 * Estadísticas → Conversaciones: cuánto y qué tan rápido se contestó a quienes escribieron. La unidad
 * es la CONSULTA (lo que la bandeja llama «espera»), en el día de su PRIMER mensaje; lo que se mandó
 * desde la bandeja y las automáticas van por la hora del envío.
 *
 * - Es un pedido aparte (`/estadisticas/conversaciones`): cada tarjeta carga y falla por su cuenta, y
 *   un error acá no toca el resto de la página (ni al revés).
 * - Antes de `medidoDesde` no se medían las consultas. Un rango que termina antes lo dice en vez de
 *   «Nadie escribió», y con `medidoDesde` null (la marca todavía no recibió ningún mensaje medido) se
 *   dice que no se midió ninguna; lo que se mandó desde Conversaciones se sigue viendo (va por la hora
 *   del envío).
 * - Sin dato no hay «0 %»: una tasa sin divisor se dice («Sin consultas terminadas», «—»).
 */

import { Link } from "react-router-dom";
import { CheckCheck, Clock, MessagesSquare, Timer } from "lucide-react";
import type { ConteoConsultas, EstadisticasConversaciones, PrimeraRespuesta, RangoFechas } from "../../types.estadisticas";
import {
  ACLARACION_CONSULTAS, ACLARACION_PRIMERA_RESPUESTA, COLOR_CONSULTA, ORIGENES_CONSULTA_TEXTO, antesDeMedir, avisoMedidoDesde,
  baseATiempo, duracionTexto, filasDesglose, filasTramos, lineaEnvios, tasaTexto, vacioConsultas,
} from "../../logic/estadisticas";
import { R_CONVERSACIONES } from "../../logic/conversaciones";
import { CATEGORIA } from "../../logic/textos";
import { usd } from "../../logic/formato";
import { SecTitulo, Tarjeta } from "../Tarjeta";
import StatTile from "../../../campana-nueva/components/StatTile";
import { num, plural } from "../../../campana-nueva/utils/format";
import { ContenidoTarjeta, ErrorTarjeta, Esqueleto, Vacio } from "./comun";
import type { EstadoPedido } from "./comun";
import { GraficoConsultas } from "./GraficoConsultas";

export function SeccionConversaciones({ dato, estado, onReintentar, rango }: {
  dato: EstadisticasConversaciones | null; estado: EstadoPedido; onReintentar: () => void; rango: RangoFechas;
}) {
  const aviso = dato ? avisoMedidoDesde(dato.medidoDesde, rango) : null;
  const vacio = vacioConsultas(dato?.medidoDesde, rango);
  const envios = (
    <Tarjeta titulo="Lo que se mandó desde Conversaciones" sub="Respuestas y reaperturas del período, por la hora del envío">
      <ContenidoTarjeta dato={dato} estado={estado} onReintentar={onReintentar} queFalta="lo que se mandó desde Conversaciones">
        {(d) => <TarjetaEnviosBandeja envios={d.envios} automaticas={d.automaticas} />}
      </ContenidoTarjeta>
    </Tarjeta>
  );

  return (
    <section className="space-y-6" aria-label="Conversaciones">
      <div>
        <SecTitulo titulo="Conversaciones" sub="Cuánto y qué tan rápido se contestó a quienes escribieron"
          right={<Link to={R_CONVERSACIONES} className="text-sm font-semibold text-violet-700 hover:underline">Ir a Conversaciones</Link>} />
        {aviso && <p className="-mt-3 text-xs text-slate-500">{aviso}</p>}
      </div>

      {dato && antesDeMedir(dato.medidoDesde, rango) ? (
        <>
          <Vacio>{vacio}</Vacio>
          {envios}
        </>
      ) : (
        <>
          <KpisConsultas dato={dato} estado={estado} onReintentar={onReintentar} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <Tarjeta titulo="Qué pasó con cada consulta" sub="Por el día del primer mensaje">
              <ContenidoTarjeta dato={dato} estado={estado} onReintentar={onReintentar} queFalta="las consultas">
                {(d) => <TarjetaConsultas consultas={d.consultas} vacio={vacio} />}
              </ContenidoTarjeta>
            </Tarjeta>
            <Tarjeta titulo="Cuánto se tardó en contestar" sub="Hasta la primera respuesta con texto">
              <ContenidoTarjeta dato={dato} estado={estado} onReintentar={onReintentar} alto="h-40" queFalta="los tiempos de respuesta">
                {(d) => <TarjetaPrimeraRespuesta primeraRespuesta={d.primeraRespuesta} />}
              </ContenidoTarjeta>
            </Tarjeta>
          </div>

          <Tarjeta titulo="Consultas por día" sub="Por el día del primer mensaje">
            <ContenidoTarjeta dato={dato} estado={estado} onReintentar={onReintentar} alto="h-56" queFalta="las consultas por día">
              {(d) => <GraficoConsultas serie={d.serie} />}
            </ContenidoTarjeta>
          </Tarjeta>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <Tarjeta titulo="De dónde vinieron">
              <ContenidoTarjeta dato={dato} estado={estado} onReintentar={onReintentar} queFalta="de dónde vinieron">
                {(d) => <TablaOrigenes porOrigen={d.porOrigen} vacio={vacio} />}
              </ContenidoTarjeta>
            </Tarjeta>
            {envios}
          </div>
        </>
      )}
    </section>
  );
}

/** Barra proporcional de una fila: el color va en la marca, el número en tinta al lado. */
function Barra({ pct, color }: { pct: number | null; color: string }) {
  const ancho = pct !== null && pct > 0 ? Math.max(1.5, Math.min(100, pct * 100)) : 0;
  return (
    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
      <div className="h-full rounded-full" style={{ width: `${ancho}%`, backgroundColor: color }} />
    </div>
  );
}

function Cantidad({ n, pct }: { n: number; pct: number | null }) {
  return (
    <p className="text-sm text-right tabular-nums whitespace-nowrap">
      <span className="font-semibold text-slate-900">{num(n)}</span> <span className="text-xs text-slate-500">{tasaTexto(pct)}</span>
    </p>
  );
}

export function KpisConsultas({ dato, estado, onReintentar }: { dato: EstadisticasConversaciones | null; estado: EstadoPedido; onReintentar: () => void }) {
  if (!dato) {
    if (estado.error) return <ErrorTarjeta texto={`No pudimos traer las consultas del período. ${estado.error}`} onReintentar={onReintentar} />;
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" aria-busy="true" aria-label="Cargando las consultas del período">
        {[0, 1, 2, 3].map((i) => <Esqueleto key={i} alto="h-32" />)}
      </div>
    );
  }
  const c = dato.consultas;
  const total = Number(c?.total) || 0;
  const pr = dato.primeraRespuesta;
  const tasa = c?.tasaATiempo;
  const hayTasa = tasa !== null && tasa !== undefined && Number.isFinite(Number(tasa));
  return (
    <div className="space-y-3">
      {estado.error && <p className="text-xs text-amber-700">No pudimos actualizar: {estado.error} <button type="button" onClick={onReintentar} className="font-semibold underline">Reintentar</button></p>}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={<MessagesSquare size={15} />} label="Consultas" value={total > 0 ? num(total) : null} vacio="Nadie escribió" tono="neutro"
          sub={`de ${num(dato.personas)} ${plural(dato.personas, "persona", "personas")}`} />
        <StatTile icon={<CheckCheck size={15} />} label="Respondidas a tiempo" value={hayTasa ? tasaTexto(tasa) : null} vacio="Sin consultas terminadas" tono="respuesta"
          meterPct={hayTasa ? Number(tasa) * 100 : undefined}
          sub={`${num(c.respondidas)} de ${num(baseATiempo(c))} con texto, antes de que se cerrara la ventana de WhatsApp`} />
        <StatTile icon={<Timer size={15} />} label="Primera respuesta" value={Number(pr?.n) > 0 ? duracionTexto(pr.p50Seg) : null} vacio="Sin respuestas" tono="lectura"
          sub={`Mediana · 9 de cada 10 en menos de ${duracionTexto(pr?.p90Seg)}`}
          chip={pr?.muestreado ? { texto: "muestra", cls: "bg-amber-50 text-amber-700" } : undefined} />
        <StatTile icon={<Clock size={15} />} label="Vencieron sin respuesta" value={total > 0 ? num(c.vencidas) : null} vacio="Nadie escribió" tono="baja"
          sub={`de ${num(total)} ${plural(total, "consulta", "consultas")}`} />
      </div>
    </div>
  );
}

export function TarjetaConsultas({ consultas, vacio }: { consultas: ConteoConsultas; vacio: string }) {
  if (!(Number(consultas?.total) > 0)) return <Vacio>{vacio}</Vacio>;
  return (
    <div className="space-y-3">
      <ul className="divide-y divide-slate-100">
        {filasDesglose(consultas).map((f) => (
          <li key={f.clave} className="py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{f.label}</p>
                <p className="text-[11px] text-slate-400">{f.detalle}</p>
              </div>
              <Cantidad n={f.cantidad} pct={f.pct} />
            </div>
            <Barra pct={f.pct} color={f.color} />
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-400 leading-relaxed">{ACLARACION_CONSULTAS}</p>
    </div>
  );
}

export function TarjetaPrimeraRespuesta({ primeraRespuesta: pr }: { primeraRespuesta: PrimeraRespuesta }) {
  const n = Number(pr?.n) || 0;
  if (!n) return <Vacio>Todavía no se contestó con texto ninguna consulta de estas fechas.</Vacio>;
  const comercio = Number(pr.porAutor?.comercio) || 0;
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-700">
        Mediana <strong className="text-slate-900">{duracionTexto(pr.p50Seg)}</strong>
        <span className="block text-xs text-slate-500">9 de cada 10 en menos de {duracionTexto(pr.p90Seg)}</span>
      </p>
      <ul className="space-y-2.5">
        {filasTramos(pr.tramos, n).map((t) => (
          <li key={t.label}>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-sm text-slate-700">{t.label}</p>
              <Cantidad n={t.cantidad} pct={t.pct} />
            </div>
            <Barra pct={t.pct} color={COLOR_CONSULTA.respondidas} />
          </li>
        ))}
      </ul>
      {comercio > 0 && <p className="text-xs text-slate-500">Contestó el equipo de Deenex: {num(Number(pr.porAutor?.operadorDeenex) || 0)} · el comercio: {num(comercio)}</p>}
      {pr.muestreado && <p className="text-xs text-amber-700">Hay muchas consultas en el rango: los tiempos se midieron sobre las 50.000 más recientes.</p>}
      <p className="text-xs text-slate-400 leading-relaxed">{ACLARACION_PRIMERA_RESPUESTA}</p>
    </div>
  );
}

export function TablaOrigenes({ porOrigen, vacio }: { porOrigen: EstadisticasConversaciones["porOrigen"]; vacio: string }) {
  if (!ORIGENES_CONSULTA_TEXTO.some((o) => Number(porOrigen?.[o.origen]?.total) > 0)) return <Vacio>{vacio}</Vacio>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-400">
            <th scope="col" className="py-2 pr-3">Origen</th>
            <th scope="col" className="py-2 pr-3 text-right">Consultas</th>
            <th scope="col" className="py-2 pr-3 text-right">Respondidas a tiempo</th>
            <th scope="col" className="py-2 text-right">Mediana</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {ORIGENES_CONSULTA_TEXTO.map((o) => {
            const f = porOrigen?.[o.origen];
            return (
              <tr key={o.origen}>
                <td className="py-2.5 pr-3">
                  <p className="text-slate-800">{o.label}</p>
                  <p className="text-[11px] text-slate-400">{o.detalle}</p>
                </td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-slate-900">{num(Number(f?.total) || 0)}</td>
                <td className="py-2.5 pr-3 text-right tabular-nums text-slate-700">{tasaTexto(f?.tasaATiempo)}</td>
                <td className="py-2.5 text-right tabular-nums text-slate-700">{duracionTexto(f?.p50Seg)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Cifra({ label, valor, lineas }: { label: string; valor: string; lineas: string[] }) {
  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2.5">
      <p className="text-lg font-semibold text-slate-900">{valor}</p>
      <p className="text-xs text-slate-600">{label}</p>
      {lineas.map((l, i) => (l ? <p key={i} className="text-[11px] text-slate-400 mt-0.5">{l}</p> : null))}
    </div>
  );
}

export function TarjetaEnviosBandeja({ envios, automaticas }: Pick<EstadisticasConversaciones, "envios" | "automaticas">) {
  const textos = envios?.textos;
  const plantillas = envios?.plantillas;
  const gratis = Number(envios?.gratisPorCupo) || 0;
  const sinCosto = Number(envios?.sinCostoFinal) || 0;
  const confirmaciones = Number(automaticas?.confirmacionesBaja) || 0;
  // las automáticas que no salieron no están en `respuestas` ni en `confirmacionesBaja`: van en su línea
  const noSalieron = Number(automaticas?.fallidas) || 0;
  const algo = [textos?.total, plantillas?.total, automaticas?.respuestas, confirmaciones, noSalieron, envios?.cobradoClienteUSD, automaticas?.cobradoClienteUSD]
    .some((x) => Number(x) > 0);
  if (!algo) return <Vacio>No se mandó nada desde Conversaciones en estas fechas.</Vacio>;
  const categorias = (plantillas?.porCategoria || [])
    .filter((c) => Number(c?.cantidad) > 0)
    .map((c) => `${(CATEGORIA as Record<string, string>)[c.categoria] || c.categoria} ${num(c.cantidad)}`)
    .join(" · ");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Cifra label="Respuestas con texto" valor={num(Number(textos?.total) || 0)} lineas={[lineaEnvios(textos)]} />
      <Cifra label="Reaperturas con plantilla" valor={num(Number(plantillas?.total) || 0)} lineas={[categorias, lineaEnvios(plantillas)]} />
      <Cifra label="Cobrado por respuestas y reaperturas" valor={usd(envios?.cobradoClienteUSD)} lineas={[
        gratis > 0 ? `${num(gratis)} ${plural(gratis, "respuesta entró", "respuestas entraron")} en los 1.000 mensajes de servicio sin cargo de Meta del mes (se cobró sólo el envío)` : "",
        sinCosto > 0 ? `${num(sinCosto)} todavía sin costo final: se cuenta lo reservado` : "",
      ]} />
      <Cifra label="Respuestas automáticas" valor={num(Number(automaticas?.respuestas) || 0)} lineas={[
        // «del equipo» no se entendía del lado del cliente: no las cuenta como respuesta de una persona (de él o de Deenex)
        `No cuentan como respuesta de una persona · ${num(confirmaciones)} ${plural(confirmaciones, "confirmación de baja", "confirmaciones de baja")}`,
        noSalieron > 0 ? `${num(noSalieron)} ${plural(noSalieron, "no salió", "no salieron")}` : "",
        `Cobrado: ${usd(automaticas?.cobradoClienteUSD)}`,
      ]} />
    </div>
  );
}

export default SeccionConversaciones;
